import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { CreditCostNotice } from '../components/credits/CreditCostNotice';
import { VideoEditorView } from '../components/marketing/VideoEditorView';
import { AvatarGallery } from '../components/marketing/AvatarGallery';
import { BrandEditDrawer } from '../components/marketing/BrandEditDrawer';
import { InspirationProfilesView } from '../components/marketing/InspirationProfilesView';
import { CreateContentAIView } from '../components/marketing/CreateContentAIView';
import { SocialPostsListPage } from './SocialPostsListPage';
import { PostingPage } from './PostingPage';
import { EcommercePhotosView } from '../components/marketing/EcommercePhotosView';
import { EcommerceVideosView } from '../components/marketing/EcommerceVideosView';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import { MarketingBrand } from '../types';
import { useMenuPermissions } from '../contexts/MenuPermissionsContext';
type StudioIconName = 'brand' | 'inspirations' | 'briefs' | 'pipeline' | 'avatars' | 'postagens' | 'editor' | 'ia_marketing' | 'ecommerce';
type MarketingSectionId = 'brand' | 'inspirations' | 'criar_conteudo_ia' | 'avatares' | 'postagens' | 'edicao_video' | 'fotos_ecommerce' | 'videos_ecommerce';

function StudioIcon({ name }: { name: StudioIconName }) {
  const common = { className: 'h-5 w-5', fill: 'none', viewBox: '0 0 24 24', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  const paths = {
    brand: <><path d="M4 7h16" /><path d="M6 7v12" /><path d="M18 7v12" /><path d="M8 11h3" /><path d="M13 11h3" /><path d="M8 15h8" /><path d="M9 3h6l1 4H8l1-4Z" /></>,
    inspirations: <><path d="M16 11a4 4 0 1 0-8 0" /><path d="M4 20a8 8 0 0 1 16 0" /><path d="M18 5l2-2" /><path d="M20 8h3" /><path d="M6 5 4 3" /><path d="M4 8H1" /></>,
    briefs: <><path d="M5 4h14v16H5z" /><path d="M9 8h6" /><path d="M9 12h6" /><path d="M9 16h3" /></>,
    pipeline: <><path d="M4 6h5v5H4z" /><path d="M15 6h5v5h-5z" /><path d="M4 15h5v5H4z" /><path d="M9 8.5h6" /><path d="M6.5 11v4" /><path d="M17.5 11v2a2 2 0 0 1-2 2H9" /></>,
    avatars: <><path d="M12 13a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" /><path d="M4 21a8 8 0 0 1 16 0" /><path d="M9 9h.01" /><path d="M15 9h.01" /><path d="M10 12h4" /></>,
    postagens: <><path d="M4 6h16" /><path d="M4 10h16" /><path d="M4 14h10" /><path d="M4 18h7" /></>,
    editor: <><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" /><circle cx="12" cy="13" r="3" /><path d="m9 3 1 4" /><path d="m15 3-1 4" /></>,
    ia_marketing: <><path fill="currentColor" stroke="none" d="M12 2l1.8 5.4L19.2 9l-5.4 1.8L12 16.2l-1.8-5.4L4.8 9l5.4-1.8L12 2z"/><path fill="currentColor" stroke="none" d="M19 14l.9 2.7 2.7.9-2.7.9L19 21l-.9-2.7-2.7-.9 2.7-.9L19 14z"/><path fill="currentColor" stroke="none" d="M5 17l.6 1.8 1.8.6-1.8.6L5 21.8l-.6-1.8-1.8-.6 1.8-.6L5 17z"></path></>,
    ecommerce: <><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-4.5-4.5L7 20" /></>,
  };

  return <svg {...common} aria-hidden="true">{paths[name]}</svg>;
}

function AgentBrandChat({ onDone, onCancel }: { onDone: (brand?: MarketingBrand) => void; onCancel: () => void }) {
  // ── fase: 'form' antes de iniciar | 'chat' depois do scraping ──────────────
  const [phase, setPhase] = useState<'form' | 'chat'>('form');
  const [form, setForm] = useState({ name: '', website: '', instagram: '' });
  const [formError, setFormError] = useState('');

  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Array<{ role: 'assistant' | 'user'; content: string }>>([]);
  const [memory, setMemory] = useState<any>({});
  const [draftBrandId, setDraftBrandId] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [missing, setMissing] = useState<string[]>([]);
  const [debug, setDebug] = useState<any>(null);
  const [showDebug, setShowDebug] = useState(false);
  const [saving, setSaving] = useState(false);
  const inflightRef = useRef(false);

  // Submete o form inicial → monta a primeira mensagem e inicia o chat
  const startChat = async () => {
    if (inflightRef.current) return;
    if (!form.name.trim()) { setFormError('Informe o nome da marca.'); return; }
    if (!form.website.trim()) { setFormError('Informe o site da marca.'); return; }
    if (!form.instagram.trim()) { setFormError('Informe o @ do Instagram.'); return; }
    setFormError('');
    inflightRef.current = true;
    setPhase('chat');
    setSaving(true);

    // Normaliza o handle: remove URL do Instagram, mantém só o @handle
    const igRaw = form.instagram.trim();
    const igHandle = igRaw.replace(/https?:\/\/(www\.)?instagram\.com\//i, '').replace(/\/$/, '').replace(/^@/, '');

    // Primeira mensagem estruturada para o agente
    const firstUserMsg = `Nome da marca: ${form.name.trim()}\nSite: ${form.website.trim()}\nInstagram: @${igHandle}`;
    const firstMessages = [{ role: 'user' as const, content: firstUserMsg }];
    setMessages(firstMessages);

    try {
      const response = await api.chatMarketingBrandAgent({ messages: firstMessages, memory: {}, draftBrandId: null });
      setMessages([...firstMessages, { role: 'assistant', content: response.reply }]);
      setMemory(response.memory || {});
      if (response.draftBrandId) setDraftBrandId(response.draftBrandId);
      setProgress(response.progress || 0);
      setMissing(response.missing || []);
      setDebug(response.debug || null);
      if (response.shouldCreateBrand && response.brand) {
        toast.success('Brand board estruturado pelo agente!');
        onDone(response.brand);
      }
    } catch (error: any) {
      toast.error(error.message || 'Erro ao iniciar agente');
      setPhase('form');
    } finally {
      setSaving(false);
      inflightRef.current = false;
    }
  };

  const sendAnswer = async () => {
    if (!input.trim() || inflightRef.current) return;
    const nextMessages = [...messages, { role: 'user' as const, content: input.trim() }];
    setMessages(nextMessages);
    setInput('');
    inflightRef.current = true;
    setSaving(true);
    try {
      const response = await api.chatMarketingBrandAgent({ messages: nextMessages, memory, draftBrandId });
      setMessages([...nextMessages, { role: 'assistant', content: response.reply }]);
      setMemory(response.memory || {});
      if (response.draftBrandId) setDraftBrandId(response.draftBrandId);
      setProgress(response.progress || 0);
      setMissing(response.missing || []);
      setDebug(response.debug || null);
      if (response.shouldCreateBrand && response.brand) {
        toast.success('Brand board estruturado pelo agente!');
        onDone(response.brand);
      }
    } catch (error: any) {
      toast.error(error.message || 'Erro no chat do agente');
    } finally {
      setSaving(false);
      inflightRef.current = false;
    }
  };

  return (
    <div className="mx-auto flex max-h-[calc(100vh-120px)] max-w-3xl flex-col overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 shadow-sm">
      <div className="border-b border-slate-800 bg-slate-950 p-6 text-white">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-slate-500">Brand Builder Agent</p>
            <h2 className="mt-2 text-2xl font-black">
              {phase === 'form' ? 'Criar nova marca' : 'Chat de construção da marca'}
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-slate-300">
              {phase === 'form'
                ? 'Preencha os campos abaixo. O agente vai pesquisar o site e o Instagram antes de iniciar.'
                : 'O agente pesquisou sua marca e está construindo o Brand Board.'}
            </p>
          </div>
          <button onClick={onCancel} className="rounded-lg px-3 py-2 text-sm text-slate-400 hover:bg-slate-900 hover:text-slate-200">Cancelar</button>
        </div>
      </div>

      {/* ── FASE: FORM ─────────────────────────────────────────────────────── */}
      {phase === 'form' && (
        <div className="mx-auto max-w-lg p-8">
          <div className="space-y-5">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-slate-300">Nome da marca <span className="text-red-400">*</span></label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Ex: CUT App"
                className="w-full rounded-lg border border-slate-700 bg-slate-900 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:border-slate-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-slate-300">Site <span className="text-red-400">*</span></label>
              <input
                type="url"
                value={form.website}
                onChange={e => setForm(f => ({ ...f, website: e.target.value }))}
                placeholder="https://app.cutmix.com.br"
                className="w-full rounded-lg border border-slate-700 bg-slate-900 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:border-slate-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-slate-300">Instagram <span className="text-red-400">*</span></label>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-500">@</span>
                <input
                  type="text"
                  value={form.instagram}
                  onChange={e => setForm(f => ({ ...f, instagram: e.target.value }))}
                  placeholder="_cutapp"
                  className="flex-1 rounded-lg border border-slate-700 bg-slate-900 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:border-slate-500 focus:outline-none"
                />
              </div>
              <p className="mt-1 text-xs text-slate-400">Só o @ ou o link completo — normalizamos automaticamente.</p>
            </div>
            {formError && <p className="text-sm font-medium text-red-400">{formError}</p>}
            <button
              onClick={startChat}
              disabled={saving}
              className="w-full rounded-lg bg-slate-800 py-3.5 text-sm font-bold text-slate-100 transition-colors hover:bg-slate-700 disabled:opacity-50"
            >
              {saving ? 'Pesquisando marca...' : 'Pesquisar e iniciar →'}
            </button>
            {saving && (
              <p className="text-xs text-center text-slate-400 animate-pulse">
                Coletando dados do site e Instagram via scraping... pode levar ~30s
              </p>
            )}
          </div>
        </div>
      )}

      {/* ── FASE: CHAT ─────────────────────────────────────────────────────── */}
      {phase === 'chat' && (
        <div className="grid min-h-0 flex-1 gap-0 overflow-hidden lg:grid-cols-[1fr_320px]">
          <div className="flex min-h-0 flex-col bg-slate-950">
            <div className="flex-1 space-y-4 overflow-y-auto p-6">
              {messages.filter(m => m.role === 'assistant').length === 0 && saving && (
                <div className="max-w-[220px] rounded-lg bg-slate-900 px-4 py-3 text-sm text-slate-400">Pesquisando sua marca...</div>
              )}
              {messages.map((message, index) => (
                <div key={index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[78%] whitespace-pre-wrap rounded-lg px-4 py-3 text-sm ${message.role === 'user' ? 'bg-slate-700 text-white' : 'bg-slate-900 text-slate-300'}`}>
                    {message.content}
                  </div>
                </div>
              ))}
              {saving && messages.some(m => m.role === 'assistant') && (
                <div className="max-w-[220px] rounded-lg bg-slate-900 px-4 py-3 text-sm text-slate-400">Agente pensando...</div>
              )}
            </div>
            <div className="border-t border-slate-800 bg-slate-950 p-4">
              <div className="mb-3">
                <CreditCostNotice
                  items={[{ featureKey: 'brand_build', qty: 1 }]}
                  note="Cobrado apenas quando uma brand NOVA é criada com sucesso. Ajustes numa brand existente não consomem créditos."
                />
              </div>
              <div className="flex gap-3">
                <textarea
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendAnswer(); } }}
                  placeholder="Digite sua resposta para o agente..."
                  className="min-h-16 flex-1 resize-none rounded-lg border border-slate-700 bg-slate-900 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:border-slate-500 focus:outline-none"
                />
                <button onClick={sendAnswer} disabled={saving} className="rounded-lg bg-slate-800 px-6 py-3 text-sm font-bold text-slate-100 hover:bg-slate-700 disabled:opacity-50">
                  {saving ? '...' : 'Enviar'}
                </button>
              </div>
            </div>
          </div>
          <aside className="min-h-0 overflow-y-auto border-l border-slate-800 bg-slate-950 p-5">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Memória viva do agente</p>
            <div className="mt-4 rounded-xl border border-indigo-100 bg-indigo-50 p-3">
              <div className="flex items-center justify-between text-xs font-bold text-indigo-800">
                <span>Progresso</span>
                <span>{progress}%</span>
              </div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-white">
                <div className="h-full rounded-full bg-indigo-600" style={{ width: `${Math.min(100, Math.max(0, progress))}%` }} />
              </div>
            </div>
            {debug && (
              <div className={`mt-4 rounded-xl border p-3 ${debug.usedLlm ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`}>
                <button
                  type="button"
                  onClick={() => setShowDebug(v => !v)}
                  className="flex w-full items-center justify-between gap-3 text-left"
                  aria-expanded={showDebug}
                >
                  <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Debug LLM</span>
                  <span className="rounded-md bg-white/70 px-2 py-1 text-[10px] font-bold uppercase text-slate-500">
                    {showDebug ? 'Ocultar' : 'Expandir'}
                  </span>
                </button>
                <div className="mt-2 flex flex-wrap gap-1.5 text-[11px] text-slate-600">
                  <span className="rounded-md bg-white/70 px-2 py-1">usedLlm: <b>{String(debug.usedLlm)}</b></span>
                  {debug.model && <span className="rounded-md bg-white/70 px-2 py-1">model: <b>{debug.model}</b></span>}
                </div>
                {showDebug && (
                  <div className="mt-3 max-h-56 overflow-y-auto rounded-lg border border-white/70 bg-white/70 p-2">
                    {debug.requestId && <p className="mb-2 break-all text-xs text-slate-500">requestId: {debug.requestId}</p>}
                    <pre className="whitespace-pre-wrap break-words text-[11px] leading-relaxed text-slate-600">{JSON.stringify(debug, null, 2)}</pre>
                  </div>
                )}
              </div>
            )}
            <div className="mt-4 space-y-3">
              {Object.entries(memory).filter(([k, value]) => k !== '_scraped' && value !== undefined && value !== null && value !== '').map(([key, value]) => (
                <div key={key} className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                  <p className="text-xs font-bold text-slate-700">{key}</p>
                  <p className="mt-1 line-clamp-3 text-xs text-slate-500">{typeof value === 'string' ? value : JSON.stringify(value)}</p>
                </div>
              ))}
              {missing.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                  <p className="text-xs font-bold text-amber-800">Faltando</p>
                  <p className="mt-1 text-xs text-amber-700">{missing.join(', ')}</p>
                </div>
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function Tag({ children }: { children: React.ReactNode }) {
  return <span className="inline-block rounded-lg bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700">{children}</span>;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-slate-50 p-5">
      <p className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-400">{title}</p>
      {children}
    </div>
  );
}

function LooksSection({ brand }: { brand: MarketingBrand }) {
  const [looks, setLooks] = useState<any[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', style: '', mood: '', season: '', imageUrl: '', notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getMarketingBrandLooks(brand.id).then(r => setLooks(r.looks || [])).catch(() => {});
  }, [brand.id]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error('Título obrigatório');
    setSaving(true);
    try {
      const r = await api.createMarketingBrandLook(brand.id, form);
      setLooks(prev => [r.look, ...prev]);
      setForm({ title: '', style: '', mood: '', season: '', imageUrl: '', notes: '' });
      setShowForm(false);
      toast.success('Look adicionado');
    } catch (error: any) {
      toast.error(error.message || 'Erro ao criar look');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (lookId: string) => {
    try {
      await api.deleteMarketingBrandLook(brand.id, lookId);
      setLooks(prev => prev.filter(l => l.id !== lookId));
      toast.success('Look removido');
    } catch {
      toast.error('Erro ao remover look');
    }
  };

  const STYLE_OPTIONS = ['Clean Premium', 'Raw Documental', 'Colorido Vibrante', 'Dark Luxury', 'Minimalista', 'Editorial', 'Outro'];
  const MOOD_OPTIONS = ['Aspiracional', 'Urgência', 'Confiança', 'Diversão', 'Exclusividade', 'Empatia', 'Outro'];

  return (
    <div className="rounded-2xl border border-slate-100 bg-slate-50 p-5">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Looks / Moodboard</p>
        <button onClick={() => setShowForm(v => !v)} className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-indigo-700">
          {showForm ? 'Cancelar' : '+ Look'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="mb-4 space-y-3 rounded-xl border border-indigo-100 bg-white p-4">
          <input className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="Título do look *" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <select className="rounded-lg border border-slate-200 px-3 py-2 text-sm" value={form.style} onChange={e => setForm({ ...form, style: e.target.value })}>
              <option value="">Estilo visual</option>
              {STYLE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select className="rounded-lg border border-slate-200 px-3 py-2 text-sm" value={form.mood} onChange={e => setForm({ ...form, mood: e.target.value })}>
              <option value="">Humor / Mood</option>
              {MOOD_OPTIONS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <input className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="Temporada / contexto (ex: lançamento, evergreen)" value={form.season} onChange={e => setForm({ ...form, season: e.target.value })} />
          <input className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="URL da imagem de referência (opcional)" value={form.imageUrl} onChange={e => setForm({ ...form, imageUrl: e.target.value })} />
          <textarea className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm" rows={2} placeholder="Notas sobre o look" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
          <button disabled={saving} className="rounded-lg bg-indigo-600 px-4 py-2 text-xs font-bold text-white disabled:opacity-50">{saving ? 'Salvando...' : 'Salvar look'}</button>
        </form>
      )}

      {looks.length === 0 && !showForm && (
        <p className="text-xs text-slate-400">Nenhum look cadastrado. Adicione referências visuais da brand.</p>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {looks.map(look => (
          <div key={look.id} className="group relative rounded-xl border border-slate-200 bg-white p-3">
            {look.imageUrl && (
              <img src={look.imageUrl} alt={look.title} className="mb-2 h-28 w-full rounded-lg object-cover" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            )}
            <p className="font-semibold text-sm text-slate-800">{look.title}</p>
            <div className="mt-1.5 flex flex-wrap gap-1">
              {look.style && <span className="rounded-md bg-indigo-50 px-2 py-0.5 text-xs text-indigo-700">{look.style}</span>}
              {look.mood && <span className="rounded-md bg-purple-50 px-2 py-0.5 text-xs text-purple-700">{look.mood}</span>}
              {look.season && <span className="rounded-md bg-amber-50 px-2 py-0.5 text-xs text-amber-700">{look.season}</span>}
            </div>
            {look.notes && <p className="mt-1.5 text-xs text-slate-500">{look.notes}</p>}
            <button onClick={() => handleDelete(look.id)} className="absolute right-2 top-2 hidden rounded-lg bg-rose-50 px-2 py-1 text-xs text-rose-500 group-hover:block hover:bg-rose-100">✕</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function BrandStructureCard({ brand, onClose, onLogoUploaded, onBrandUpdated }: { brand: MarketingBrand; onClose?: () => void; onLogoUploaded?: (logoUrl: string) => void; onBrandUpdated?: (brand: MarketingBrand) => void }) {
  const [rewriteOpen, setRewriteOpen] = useState(false);
  const [rewriteInstruction, setRewriteInstruction] = useState('');
  const [rewriting, setRewriting] = useState(false);
  const [rewriteError, setRewriteError] = useState('');
  const rewriteMatrix = async () => {
    if (rewriting || !rewriteInstruction.trim()) return;
    setRewriting(true);
    setRewriteError('');
    try {
      const result = await api.rewriteMarketingCampaignMatrix(brand.id, rewriteInstruction.trim());
      onBrandUpdated?.(result.brand);
      setRewriteOpen(false);
      setRewriteInstruction('');
      toast.success('Matriz de campanha reescrita e salva');
    } catch (error: any) {
      setRewriteError(error.message || 'Não foi possível reescrever a matriz. Tente novamente.');
    } finally {
      setRewriting(false);
    }
  };
  const audience = (brand.audience as any) || {};
  const offer = (brand.offer as any) || {};
  const voice = (brand.voice as any) || {};
  const vt = (brand.visualTokens as any) || {};
  const restrictions = (brand.restrictions as any) || {};
  const matrix: any[] = offer.campaignMatrix || [];
  const colors = vt.colors || {};
  const fonts = vt.fonts || {};
  const visualDirection = vt.visualDirection || {};
  const displayFont = fonts.display || 'Plus Jakarta Sans';
  const bodyFont = fonts.body || displayFont;
  const primary = colors.primary || '#2563EB';
  const light = colors.light || '#60A5FA';
  const dark = colors.dark || '#1E3A8A';
  const lightBg = colors.lightBg || '#F8FAFC';
  const darkBg = colors.darkBg || '#0F172A';
  const gradient = colors.gradient || `linear-gradient(165deg, ${dark} 0%, ${primary} 50%, ${light} 100%)`;

  useEffect(() => {
    if (!fonts.url) return;
    const existing = document.querySelector(`link[href="${fonts.url}"]`);
    if (existing) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = fonts.url;
    document.head.appendChild(link);
  }, [fonts.url]);

  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl" style={{ fontFamily: bodyFont }}>
      <div className="relative p-8 text-white" style={{ background: gradient }}>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.22),transparent_32%)]" />
        <div className="relative flex items-start justify-between gap-6">
          <div className="flex items-start gap-5">
            {/* Logo */}
            {(brand as any).logoUrl ? (
              <img src={(brand as any).logoUrl} alt="logo" className="h-20 w-20 shrink-0 rounded-2xl border-2 border-white/20 bg-white/10 object-contain p-1 backdrop-blur" />
            ) : onLogoUploaded ? (
              <label className="flex h-20 w-20 shrink-0 cursor-pointer flex-col items-center justify-center gap-1 rounded-2xl border-2 border-dashed border-white/30 bg-white/10 backdrop-blur hover:bg-white/20 transition">
                <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const form = new FormData();
                      form.append('logo', file);
                      const r = await api.uploadBrandLogo(brand.id, form);
                      onLogoUploaded(r.logoUrl);
                    } catch {}
                  }}
                />
                <span className="text-sm font-bold">IMG</span>
                <span className="text-[10px] font-bold text-white/70">Logo</span>
              </label>
            ) : null}
            <div>
            <p className="text-xs font-bold uppercase tracking-[0.32em] text-white/70">Brand Board</p>
            <h2 className="mt-3 text-5xl font-black tracking-tight" style={{ fontFamily: displayFont }}>{brand.name}</h2>
            <p className="mt-3 max-w-3xl text-lg text-white/85">{brand.promise || offer.dreamOutcome}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {brand.category && <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-bold backdrop-blur">{brand.category}</span>}
              {brand.handle && <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-bold backdrop-blur">{brand.handle}</span>}
              {brand.website && <a href={brand.website} target="_blank" rel="noreferrer" className="rounded-full bg-white/15 px-3 py-1 text-xs font-bold backdrop-blur hover:bg-white/25">{brand.website}</a>}
            </div>
            </div>
          </div>
          {onClose && <button onClick={onClose} className="rounded-xl bg-white/10 px-3 py-2 text-sm text-white/80 hover:bg-white/20">Fechar</button>}
        </div>
      </div>

      <div className="grid gap-5 p-6 lg:grid-cols-12">
        <div className="rounded-3xl p-6 text-white lg:col-span-4" style={{ background: darkBg }}>
          <p className="text-xs font-bold uppercase tracking-widest text-white/50">Tipografia</p>
          <p className="mt-5 text-5xl font-black leading-none" style={{ fontFamily: displayFont }}>Aa</p>
          <p className="mt-4 text-2xl font-black" style={{ fontFamily: displayFont }}>{displayFont}</p>
          <p className="mt-2 text-sm text-white/65">Headlines, CTAs, aberturas de carrossel e posts de alto impacto.</p>
          <div className="mt-6 rounded-2xl bg-white/10 p-4">
            <p className="text-sm" style={{ fontFamily: bodyFont }}>Body: {bodyFont}</p>
            <p className="mt-2 text-xs leading-relaxed text-white/65">Texto corrido, legendas, explicações e componentes de apoio.</p>
          </div>
        </div>

        <div className="rounded-3xl border border-slate-100 p-6 lg:col-span-8" style={{ background: lightBg }}>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Paleta da marca</p>
          <div className="mt-5 grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {Object.entries({ primary, light, dark, lightBg, darkBg }).map(([name, value]) => (
              <div key={name} className="overflow-hidden rounded-2xl border border-white bg-white shadow-sm">
                <div className="h-20" style={{ background: value as string }} />
                <div className="p-3">
                  <p className="text-xs font-bold uppercase text-slate-500">{name}</p>
                  <p className="mt-1 text-xs text-slate-400">{value as string}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 rounded-2xl p-5 text-white" style={{ background: gradient }}>
            <p className="text-xs font-bold uppercase tracking-widest text-white/70">Gradient</p>
            <p className="mt-2 text-sm text-white/90">{gradient}</p>
          </div>
        </div>

        <div className="lg:col-span-4">
          <Section title="Essência">
            <div className="space-y-2 text-sm">
              <div><span className="font-semibold text-slate-700">Categoria: </span><span className="text-slate-600">{brand.category || '[PROSPECT FORNECE]'}</span></div>
              <div><span className="font-semibold text-slate-700">Promessa: </span><span className="text-slate-600">{brand.promise || '[PROSPECT FORNECE]'}</span></div>
              {(brand.personality as string[])?.length > 0 && <div className="flex flex-wrap gap-1 pt-2">{(brand.personality as string[]).map(p => <Tag key={p}>{p}</Tag>)}</div>}
            </div>
          </Section>
        </div>

        {Object.keys(audience).length > 0 && (
          <div className="lg:col-span-4">
          <Section title="Audiência">
            <div className="space-y-2 text-sm">
              {audience.primary && <div><span className="font-semibold text-slate-700">Perfil: </span><span className="text-slate-600">{audience.primary}</span></div>}
              {audience.dream && <div><span className="font-semibold text-slate-700">Sonho: </span><span className="text-slate-600">{audience.dream}</span></div>}
              {audience.pastFailures && <div><span className="font-semibold text-slate-700">Falhas anteriores: </span><span className="text-slate-600">{audience.pastFailures}</span></div>}
              {audience.fears && <div><span className="font-semibold text-slate-700">Medos: </span><span className="text-slate-600">{audience.fears}</span></div>}
              {audience.centralSuspicion && <div><span className="font-semibold text-slate-700">Suspeita: </span><span className="text-slate-600">{audience.centralSuspicion}</span></div>}
              {audience.ethicalEnemy && <div><span className="font-semibold text-slate-700">Inimigo: </span><span className="text-slate-600">{audience.ethicalEnemy}</span></div>}
            </div>
          </Section>
          </div>
        )}

        {offer.dreamOutcome && (
          <div className="lg:col-span-4">
          <Section title="Oferta">
            <div className="space-y-2 text-sm">
              {offer.dreamOutcome && <div><span className="font-semibold text-slate-700">Resultado sonhado: </span><span className="text-slate-600">{offer.dreamOutcome}</span></div>}
              {offer.timeToResult && <div><span className="font-semibold text-slate-700">Prazo: </span><span className="text-slate-600">{offer.timeToResult}</span></div>}
              {offer.guarantee && <div><span className="font-semibold text-slate-700">Garantia: </span><span className="text-slate-600">{offer.guarantee}</span></div>}
              {offer.weakestLever && <div><span className="font-semibold text-slate-700">Alavanca mais fraca: </span><span className="text-slate-600">{offer.weakestLever}</span></div>}
              {Array.isArray(offer.problemsSolved) && offer.problemsSolved.length > 0 && (
                <div>
                  <span className="font-semibold text-slate-700">Problemas resolvidos:</span>
                  <ul className="mt-1 list-disc pl-4 text-slate-600">{offer.problemsSolved.map((p: string, i: number) => <li key={i}>{p}</li>)}</ul>
                </div>
              )}
            </div>
          </Section>
          </div>
        )}

        {voice.tone && (
          <div className="lg:col-span-4">
          <Section title="Voz">
            <div className="space-y-2 text-sm">
              {voice.tone && <div><span className="font-semibold text-slate-700">Tom: </span><span className="text-slate-600">{voice.tone}</span></div>}
              {Array.isArray(voice.speaks) && voice.speaks.length > 0 && (
                <div className="flex flex-wrap gap-1">{voice.speaks.map((s: string) => <Tag key={s}>{s}</Tag>)}</div>
              )}
              {Array.isArray(voice.doesNotSpeak) && voice.doesNotSpeak.length > 0 && (
                <div>
                  <span className="text-xs font-semibold text-rose-500">Não fala:</span>
                  <div className="mt-1 flex flex-wrap gap-1">{voice.doesNotSpeak.map((s: string) => <span key={s} className="rounded-lg bg-rose-50 px-2 py-1 text-xs text-rose-700">{s}</span>)}</div>
                </div>
              )}
            </div>
          </Section>
          </div>
        )}

        {(visualDirection.system || vt.designPrinciples?.length) && (
          <div className="lg:col-span-4">
          <Section title="Direção visual">
            <div className="space-y-3">
              {visualDirection.system && <p className="text-sm font-semibold text-slate-700">{visualDirection.system}</p>}
              {visualDirection.rationale && <p className="text-xs text-slate-500">{visualDirection.rationale}</p>}
              {Array.isArray(vt.designPrinciples) && vt.designPrinciples.length > 0 && (
                <div>
                  <p className="mb-1 text-xs font-semibold text-slate-500">Princípios</p>
                  <ul className="list-disc pl-4 text-xs text-slate-600">{vt.designPrinciples.map((p: string, i: number) => <li key={i}>{p}</li>)}</ul>
                </div>
              )}
            </div>
          </Section>
          </div>
        )}

        {(matrix.length > 0 || onBrandUpdated) && (
          <div className="lg:col-span-8">
          <Section title="Matriz de campanha">
            <div className="space-y-3">
              <p className="text-xs text-slate-500">Ângulos gerenciados automaticamente pelo agente estratégico quando audiência, oferta, posicionamento, voz ou restrições mudam.</p>
              {onBrandUpdated && (
                <div className="rounded-xl border border-indigo-100 bg-white p-3">
                  {!rewriteOpen ? (
                    <button type="button" onClick={() => setRewriteOpen(true)} className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700">
                      Reescrever com IA
                    </button>
                  ) : (
                    <div className="space-y-3">
                      <label className="block">
                        <span className="mb-2 block text-sm font-semibold text-slate-700">Como você quer reescrever a matriz?</span>
                        <textarea rows={3} maxLength={4000} value={rewriteInstruction} disabled={rewriting} onChange={e => setRewriteInstruction(e.target.value)} placeholder="Ex.: Foque em pequenos negócios que querem vender produtos de qualidade em escala e com boa margem. Trabalhe objeções sobre durabilidade e fidelidade das cores." className="w-full resize-y rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none disabled:opacity-60" />
                      </label>
                      <p className="text-xs text-slate-500">A IA usará os dados salvos da marca e a matriz atual. A nova matriz será salva ao concluir.</p>
                      {rewriteError && <p role="alert" className="text-sm text-rose-600">{rewriteError}</p>}
                      <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={rewriteMatrix} disabled={rewriting || !rewriteInstruction.trim()} className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
                          {rewriting ? 'Reescrevendo com IA...' : 'Reescrever e salvar'}
                        </button>
                        <button type="button" disabled={rewriting} onClick={() => { setRewriteOpen(false); setRewriteError(''); }} className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-600 disabled:opacity-50">Cancelar</button>
                      </div>
                      {rewriting && <p role="status" className="text-xs text-indigo-600">A IA está reescrevendo os ângulos com sua orientação e as informações da marca.</p>}
                    </div>
                  )}
                </div>
              )}
              {matrix.map((row: any, i: number) => (
                <div key={i} className="rounded-xl bg-white p-3 text-xs shadow-sm">
                  <p className="mb-2 font-bold text-slate-800">{row.angle}</p>
                  <p className="text-slate-500"><span className="font-semibold">Desejo:</span> {row.targetDesire}</p>
                  <p className="text-slate-500"><span className="font-semibold">Objeção:</span> {row.objection}</p>
                  <p className="text-slate-500"><span className="font-semibold">Mecanismo:</span> {row.mechanism}</p>
                  {Array.isArray(row.recommendedFormats) && row.recommendedFormats.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">{row.recommendedFormats.map((format: string) => <Tag key={format}>{format}</Tag>)}</div>
                  )}
                </div>
              ))}
            </div>
          </Section>
          </div>
        )}

        {restrictions.rules?.length > 0 && (
          <div className="lg:col-span-3">
          <Section title="Regras">
            <ul className="list-disc pl-4 text-xs text-slate-600 space-y-1">
              {(restrictions.rules as string[]).map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          </Section>
          </div>
        )}


      </div>

    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export function MarketingStudioPage() {
  const [brands, setBrands] = useState<MarketingBrand[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingBrand, setEditingBrand] = useState<MarketingBrand | null>(null);
  const [postagensMode, setPostagensMode] = useState<'list' | 'compose'>('list');
  const [openMenuGroups, setOpenMenuGroups] = useState<Record<string, boolean>>({});
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { canSee } = useMenuPermissions();
  useEffect(() => { loadData(); }, []);

  // Fecha o menu mobile ao navegar
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [brandsResponse] = await Promise.all([
        api.getMarketingBrands(),
      ]);
      setBrands(brandsResponse.brands);
    } catch (error: any) {
      toast.error(error.message || 'Erro ao carregar Marketing Studio');
    } finally {
      setLoading(false);
    }
  };

  const deleteBrand = async (brandId: string, brandName: string) => {
    if (!window.confirm(`Excluir a brand "${brandName}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await api.deleteMarketingBrand(brandId);
      setBrands(prev => prev.filter(b => b.id !== brandId));
      toast.success('Brand excluída');
    } catch (error: any) {
      toast.error(error.message || 'Erro ao excluir brand');
    }
  };

  const primaryBrand = brands[0] || null;
  const marketingMenu = [
    {
      id: 'brand' as const,
      label: 'Brand',
      description: 'Brand board, chat do agente e estrutura da marca',
      icon: 'brand' as StudioIconName,
      path: '/marketing-studio/brand'
    },
    {
      id: 'avatares' as const,
      label: 'Avatar Influencer',
      description: 'Avatares e Looks por tenant para vídeos com IA',
      icon: 'avatars' as StudioIconName,
      path: '/marketing-studio/avatar-influencer'
    },
    {
      id: 'inspirations' as const,
      label: 'Perfis de inspira\u00e7\u00e3o',
      description: 'Benchmarks, concorrentes, criadores e busca com agente',
      icon: 'inspirations' as StudioIconName,
      path: '/marketing-studio/perfis-inspiracao'
    },
    {
      id: 'criar_conteudo_ia' as const,
      label: 'Criar Conte\u00fado IA',
      description: 'Agente de direção criativa: pesquisa referências, planeja, orça e produz',
      icon: 'briefs' as StudioIconName,
      path: '/marketing-studio/criar-conteudo-ia',
      // Quem podia ver o Criar Conteúdo antigo vê o novo: mesma permissão, sem migrar perfis.
      permKey: 'pesquisa_briefs'
    },
    {
      id: 'fotos_ecommerce' as const,
      label: 'Produzir Fotos',
      description: 'Gere fotos de produto por pipeline editável (zoom, ambientalizada, na mão, SEO)',
      icon: 'ecommerce' as StudioIconName,
      path: '/marketing-studio/fotos-ecommerce'
    },
    {
      id: 'videos_ecommerce' as const,
      label: 'Produzir V\u00eddeos',
      description: 'Gere vídeos de produto por pipeline editável (Runway, avatar chromakey/tela cheia, overlays)',
      icon: 'ecommerce' as StudioIconName,
      path: '/marketing-studio/videos-ecommerce'
    },
    {
      id: 'postagens' as const,
      label: 'Posts',
      description: 'Publicados, agendados e rascunhos no Zernio',
      icon: 'postagens' as StudioIconName,
      path: '/marketing-studio/postagens',
      isPosts: true
    },
    {
      id: 'edicao_video' as const,
      label: 'Edi\u00e7\u00e3o de V\u00eddeo',
      description: 'Edição automática: cortes, vícios e overlays por IA',
      icon: 'editor' as StudioIconName,
      path: '/marketing-studio/edicao-video'
    }
  ];

  const visibleMarketingMenu = marketingMenu.filter(item =>
    canSee(`marketing.${(item as any).permKey || item.id}`)
  );
  const fallbackMenuPath = visibleMarketingMenu[0]?.path || '/';
  const marketingMenuGroups = [
    {
      id: 'brand_configs',
      label: 'Brand Configs',
      items: visibleMarketingMenu.filter(item => ['brand', 'avatares', 'inspirations'].includes(item.id)),
    },
    {
      id: 'ecommerce',
      label: 'E-commerce',
      items: visibleMarketingMenu.filter(item => ['fotos_ecommerce', 'videos_ecommerce'].includes(item.id)),
    },
  ].filter(group => group.items.length > 0);
  const standaloneMenuItems = ['criar_conteudo_ia', 'edicao_video', 'postagens']
    .map(id => visibleMarketingMenu.find(item => item.id === id))
    .filter(Boolean) as typeof marketingMenu;
  const currentMenuItem = visibleMarketingMenu.find(item => item.path === location.pathname);
  if (location.pathname === '/marketing-studio') {
    return <Navigate to={fallbackMenuPath} replace />;
  }
  if (!currentMenuItem) {
    return <Navigate to={fallbackMenuPath} replace />;
  }
  const activeSection: MarketingSectionId = currentMenuItem.id;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Botão flutuante do menu (mobile) */}
      <button
        type="button"
        onClick={() => setMobileNavOpen(true)}
        className="fixed left-3 top-[52px] z-30 flex h-9 w-9 items-center justify-center rounded-lg bg-slate-950/90 text-white shadow-lg md:hidden"
        aria-label="Abrir menu"
      >
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* Overlay do drawer (mobile) */}
      {mobileNavOpen && (
        <div
          className="fixed inset-0 top-11 z-40 bg-black/40 md:hidden"
          onClick={() => setMobileNavOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside className={`fixed left-0 top-11 z-50 flex h-[calc(100vh-2.75rem)] h-[calc(100dvh-2.75rem)] w-60 max-w-[85vw] flex-col bg-slate-950 text-white shadow-2xl transition-transform duration-200 md:z-40 md:translate-x-0 ${mobileNavOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <nav className="flex-1 space-y-2 overflow-y-auto px-4 pb-4 pt-5">
          {marketingMenuGroups.map(group => {
            const isGroupActive = group.items.some(item => item.id === activeSection);
            const isOpen = openMenuGroups[group.id] || isGroupActive;
            return (
              <div key={group.id} className="space-y-1">
                <button
                  type="button"
                  onClick={() => setOpenMenuGroups(prev => ({ ...prev, [group.id]: !isOpen }))}
                  className={`flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left transition ${
                    isGroupActive ? 'bg-white text-slate-950 shadow-lg' : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${isGroupActive ? 'bg-indigo-100 text-indigo-700' : 'bg-white/10 text-white'}`}>
                    <StudioIcon name={group.items[0]?.icon || 'brand'} />
                  </span>
                  <span className="min-w-0 flex-1 text-sm font-bold">{group.label}</span>
                  <svg className={`h-3 w-3 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isOpen && (
                  <div className="ml-4 space-y-1 border-l border-white/10 pl-3">
                    {group.items.map(item => {
                      const isActive = activeSection === item.id;
                      return (
                        <Link
                          key={item.id}
                          to={item.path}
                          className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition ${
                            isActive
                              ? 'bg-white/15 text-white'
                              : 'text-white/60 hover:bg-white/10 hover:text-white'
                          }`}
                        >
                          <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${isActive ? 'bg-white/20 text-white' : 'bg-white/10 text-white/80'}`}>
                            <StudioIcon name={item.icon} />
                          </span>
                          <span className="font-semibold">{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          <div className="space-y-2 pt-3">
            {standaloneMenuItems.map(item => {
              const isActive = activeSection === item.id;
              const isSpecial = (item as any).isSpecial;
              const isPosts = (item as any).isPosts;
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`block w-full rounded-2xl px-4 py-3 text-left transition ${
                    isActive
                      ? isPosts
                        ? 'bg-violet-500 text-white shadow-lg shadow-violet-950/30'
                        : 'bg-white text-slate-950 shadow-lg'
                      : isPosts
                        ? 'bg-violet-500/15 text-violet-200 hover:bg-violet-500/25 hover:text-white'
                        : isSpecial
                          ? 'hover:bg-white/10'
                          : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                  style={!isActive && isSpecial ? { color: '#fbbf24' } : undefined}
                >
                  <div className="flex items-center gap-3">
                    <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${
                      isActive
                        ? isPosts
                          ? 'bg-white/20 text-white'
                          : 'bg-indigo-100 text-indigo-700'
                        : isPosts
                          ? 'bg-violet-400/20 text-violet-100'
                          : isSpecial
                            ? 'bg-yellow-400/20'
                            : 'bg-white/10 text-white'
                    }`}><StudioIcon name={item.icon} /></span>
                    <div>
                      <p className="text-sm font-bold">{item.label}</p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </nav>
      </aside>

      <main className="ml-0 md:ml-60 p-4 sm:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Hero */}
        <div className="hidden">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.3em] text-indigo-300">Marketing Studio</p>
              <h1 className="mt-3 text-4xl font-black">Pipeline de agentes criativos</h1>
              <p className="mt-3 max-w-3xl text-slate-300">Viral Research com tools, Direção Criativa com catálogo rígido, mini-designers por formato e assets salvos para aprovação/publicação.</p>
            </div>
            {activeSection === 'brand' && primaryBrand && (
              <button
                onClick={() => setEditingBrand(primaryBrand)}
                className="flex items-center gap-2 rounded-2xl bg-white px-5 py-3 font-semibold text-slate-950 shadow-lg hover:bg-indigo-50"
              >
                <span className="text-indigo-600">Editar</span> brand
              </button>
            )}
          </div>
        </div>

        {activeSection === 'brand' && !primaryBrand && (
          <AgentBrandChat
            onDone={() => { loadData(); }}
            onCancel={() => {}}
          />
        )}

        {/* Brand detail view */}
        {activeSection === 'brand' && primaryBrand && (
          <div className="space-y-4">
            <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-1">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Brand</h2>
                <p className="text-sm text-slate-500">Uma brand principal para orientar briefs, pipeline e criativos.</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditingBrand(primaryBrand)} className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Editar</button>
                <button onClick={() => deleteBrand(primaryBrand.id, primaryBrand.name)} className="rounded-xl border border-rose-200 bg-white px-4 py-2 text-sm font-semibold text-rose-600 hover:bg-rose-50">Remover</button>
              </div>
            </div>
            <BrandStructureCard key={primaryBrand.id} brand={primaryBrand} onBrandUpdated={updated => setBrands(prev => prev.map(b => b.id === updated.id ? { ...b, ...updated } : b))} onLogoUploaded={(logoUrl) => setBrands(prev => prev.map(b => b.id === primaryBrand.id ? { ...b, logoUrl } as any : b))} />
          </div>
        )}

        {/* Brand Edit Drawer */}
        {editingBrand && (
          <BrandEditDrawer
            brand={editingBrand}
            onSaved={updated => {
              setBrands(prev => prev.map(b => b.id === updated.id ? { ...b, ...updated } : b));
              setEditingBrand(null);
            }}
            onClose={() => setEditingBrand(null)}
          />
        )}

        {/* Pesquisa & Briefs — pesquisa viral + seleção de brief */}
        {activeSection === 'inspirations' && (
          <InspirationProfilesView brand={primaryBrand} />
        )}

        {activeSection === 'criar_conteudo_ia' && (
          <CreateContentAIView brand={primaryBrand} />
        )}

        {/* Avatar Influencer */}
        {activeSection === 'avatares' && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <AvatarGallery />
          </div>
        )}

        {activeSection === 'postagens' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
              <div className="flex rounded-xl bg-slate-100 p-1">
                <button
                  type="button"
                  onClick={() => setPostagensMode('list')}
                  className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${postagensMode === 'list' ? 'bg-slate-950 text-white shadow-sm' : 'text-slate-600 hover:text-slate-950'}`}
                >
                  Ver postagens
                </button>
                <button
                  type="button"
                  onClick={() => setPostagensMode('compose')}
                  className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${postagensMode === 'compose' ? 'bg-slate-950 text-white shadow-sm' : 'text-slate-600 hover:text-slate-950'}`}
                >
                  Criar por upload
                </button>
              </div>
            </div>
            {postagensMode === 'list' ? <SocialPostsListPage /> : <PostingPage createOnly />}
          </div>
        )}

        {activeSection === 'edicao_video' && (
          <VideoEditorView brand={primaryBrand} />
        )}

        {activeSection === 'fotos_ecommerce' && (
          <EcommercePhotosView brand={primaryBrand} />
        )}

        {activeSection === 'videos_ecommerce' && (
          <EcommerceVideosView brand={primaryBrand} />
        )}

      </div>
      </main>
    </div>
  );
}


